import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, Calculator, Heart, Moon, Star, Gamepad2, Menu } from 'lucide-react';
import UserMenu from '@/components/UserMenu';
import GlobalNavMenu from '@/components/GlobalNavMenu';
import { useAuth } from '@/context/AuthContext';
import BirthDateInfoModal, { hasSeenBirthDateInfo } from '@/components/BirthDateInfoModal';
import DailyEnergyAnalysis from '@/components/DailyEnergyAnalysis';
import Footer from '@/components/Footer';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const diagnosticRef = useRef(null);
  const { user, isAuthenticated, loading } = useAuth();
  const [showBirthDateInfo, setShowBirthDateInfo] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [diagDay, setDiagDay] = useState('');
  const [diagMonth, setDiagMonth] = useState('');
  const [diagYear, setDiagYear] = useState('');

  const handleDiagnosticStart = (e) => {
    if (e) e.preventDefault();
    if (!diagDay || !diagMonth || !diagYear) return;
    const d = parseInt(diagDay, 10);
    const m = parseInt(diagMonth, 10);
    const y = parseInt(diagYear, 10);
    if (!d || !m || !y || d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100) return;
    const allDigits = `${d}${m}${y}`;
    let n = allDigits.split('').reduce((s, ch) => s + parseInt(ch, 10), 0);
    while (n > 9) n = String(n).split('').reduce((s, ch) => s + parseInt(ch, 10), 0);
    navigate('/diagnostika', {
      state: {
        baseNumber: n,
        birthDate: `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`,
      },
    });
  };

  // Smooth scroll to the diagnostic date-entry section when returning via "Bandyti kitą datą"
  useEffect(() => {
    if (location.state?.scrollToDiagnostic && diagnosticRef.current) {
      diagnosticRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [location.state]);

  // Auto-redirect logged-in users with saved birth date to results page
  useEffect(() => {
    if (!loading && isAuthenticated && user && user.day && user.month && user.year) {
      // User is logged in and has birth date - save to localStorage and go to results
      const numerologyData = {
        firstName: user.firstName,
        lastName: user.lastName,
        day: user.day,
        month: user.month,
        year: user.year,
        lifePath: user.lifePath,
        gender: user.gender || null // Include gender for personalized greetings
      };
      localStorage.setItem('numerologyData', JSON.stringify(numerologyData));
      navigate('/rezultatai');
    }
  }, [loading, isAuthenticated, user, navigate]);

  // Function to handle "Start Journey" button
  const handleStartJourney = () => {
    if (isAuthenticated && user && user.day && user.month && user.year) {
      // User already has birth date saved - go directly to results
      navigate('/rezultatai');
    } else {
      // Check if user has seen the birth date info modal
      if (hasSeenBirthDateInfo()) {
        // User has already seen the info - go to calculation form
        navigate('/skaiciuoti');
      } else {
        // Show the info modal first
        setShowBirthDateInfo(true);
      }
    }
  };

  // Handle confirmation from birth date info modal
  const handleBirthDateInfoConfirm = () => {
    setShowBirthDateInfo(false);
    navigate('/skaiciuoti');
  };

  return (
    <div className="min-h-screen overflow-hidden relative">
      {/* Decorative Background Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Top Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setNavOpen(true)}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#d4af37]/30 text-[#d4af37]/70 hover:text-[#d4af37] hover:bg-[#d4af37]/10 transition-colors"
            data-testid="nav-hamburger"
          >
            <Menu className="w-5 h-5" />
          </button>
          <UserMenu />
        </div>

        {/* Header */}
        <header className="text-center py-8">
          <div className="inline-flex items-center justify-center gap-3 mb-4">
            <h1 className="font-display text-4xl sm:text-5xl bg-gradient-to-r from-amber-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">NUMENAXIS</h1>
          </div>
          <p className="text-foreground/70 text-base sm:text-lg max-w-2xl mx-auto italic">
            Pažinti save – tai prisiminti, kas buvai dar prieš tapdamas tuo, kuo reikėjo.
          </p>
        </header>

        {/* Unified Introduction */}
        <section className="max-w-3xl mx-auto mb-10">
          <Card className="card-mystical p-8 sm:p-10 text-center shadow-card border-border/50">
            <div className="text-foreground/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              <p>
                NUMENAXIS – Tavo intuicijos ir savęs pažinimo erdvė. Žiūrėk į tai kaip į gilų, bet lengvą žaidimą. Ši platforma neduoda 100% dogmų – tikroji tiesa visada slypi tik tavo paties viduje, tavo blaiviame prote ir intuicijoje. Numenaxis yra tik tavo navigacija, o vairo valdymas visada buvo ir yra tik tavo paties rankose.
              </p>
            </div>
          </Card>
        </section>

        {/* Diagnostic Game Container */}
        <section ref={diagnosticRef} className="max-w-2xl mx-auto mb-16" data-testid="diagnostic-game-section">
          <Card className="bg-gradient-to-br from-[#1a1a3e]/80 to-[#2d1b4e]/60 border-[#d4af37]/30 p-6 sm:p-8">
            <h3 className="font-display text-xl sm:text-2xl text-center text-[#d4af37] mb-2">
              Gyvenimo situacijos ir balanso diagnostika
            </h3>
            <p className="text-center text-muted-foreground text-sm mb-6">
              Įvesk savo gimimo datą ir pradėk diagnostinį žaidimą
            </p>

            <form onSubmit={handleDiagnosticStart}>
              <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto mb-6">
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block text-center">Diena</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    enterKeyHint="go"
                    maxLength={2}
                    value={diagDay}
                    onChange={(e) => setDiagDay(e.target.value.replace(/\D/g, ''))}
                    className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                    placeholder="DD"
                    data-testid="diag-day"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block text-center">Mėnuo</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    enterKeyHint="go"
                    maxLength={2}
                    value={diagMonth}
                    onChange={(e) => setDiagMonth(e.target.value.replace(/\D/g, ''))}
                    className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                    placeholder="MM"
                    data-testid="diag-month"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block text-center">Metai</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    enterKeyHint="go"
                    maxLength={4}
                    value={diagYear}
                    onChange={(e) => setDiagYear(e.target.value.replace(/\D/g, ''))}
                    className="bg-[#0f0f23]/60 border-[#d4af37]/30 text-white text-center"
                    placeholder="YYYY"
                    data-testid="diag-year"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full max-w-sm mx-auto flex bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3 text-base"
                data-testid="start-diagnostic-button"
              >
                <Gamepad2 className="w-5 h-5 mr-2" />
                Pradėti diagnostinį žaidimą
              </Button>
            </form>
          </Card>
        </section>

        {/* Daily Energy Analysis Block - Moved UP (now position #2) */}
        <section className="max-w-4xl mx-auto mb-16">
          <DailyEnergyAnalysis />
        </section>

        {/* Features Grid - Now position #3 */}
        <section className="max-w-6xl mx-auto mb-16">
          <h3 className="font-display text-2xl sm:text-3xl text-center text-amber-400 mb-8">
            Ką atrasi NUMENAXIS?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Sparkles className="w-8 h-8" />,
                title: 'Gyvenimo kelio skaičius',
                description: 'Tavo pagrindinė misija ir gyvenimo kryptis'
              },
              {
                icon: <Star className="w-8 h-8" />,
                title: 'Tavo vidinė matrica',
                description: 'Gilus 9 langelių ir 8 linijų charakterio žemėlapis'
              },
              {
                icon: <Moon className="w-8 h-8" />,
                title: 'Savaitinis ciklas',
                description: 'Vidinės Ašies sistema: Būsena, Šešėlis, Kryptis ir Stebėjimo taškas'
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: 'Asmenybės skaičius',
                description: 'Kaip kiti suvokia tavo išorinį įvaizdį'
              },
              {
                icon: <Calculator className="w-8 h-8" />,
                title: 'Asmeninė dienos vibracija',
                description: 'Tavo asmeninė šiandienos energija ir galimybės'
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: 'Santykių harmonija',
                description: 'Gilioji analizė su kitu žmogumi per „Veidrodžio" principą'
              }
            ].map((feature, index) => (
              <Card 
                key={index} 
                className="card-mystical p-6 hover:shadow-glow transition-all duration-300 cursor-pointer group border-border/50"
              >
                <div className="text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h4 className="font-semibold text-lg mb-2 text-foreground">
                  {feature.title}
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA Section - Hidden during pivot (subscription logic preserved in backend) */}
        {/* 
        <section className="max-w-2xl mx-auto text-center mb-16">
          <Card className="card-mystical p-8 border-primary/30 shadow-glow">
            <Sparkles className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse-glow" />
            <h3 className="font-display text-2xl sm:text-3xl text-primary mb-4">
              Pasiruošę atskleisti savo skaičius?
            </h3>
            <p className="text-foreground/80 mb-6 text-base leading-relaxed">
              Leisk skaičiams prabilti. Atskleisk savo gimimo datos kodą ir gauk išsamią numerologinę analizę kaip dovaną.
            </p>
            <Button 
              size="lg"
              onClick={handleStartJourney}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold px-8 py-6 text-lg transition-all duration-300"
            >
              Atskleisti savo kodą
            </Button>
          </Card>
        </section>
        */}

      </div>

      {/* Footer */}
      <Footer />

      {/* Birth Date Info Modal */}
      <BirthDateInfoModal
        isOpen={showBirthDateInfo}
        onClose={() => setShowBirthDateInfo(false)}
        onConfirm={handleBirthDateInfoConfirm}
      />

      {/* Global Navigation Menu */}
      <GlobalNavMenu isOpen={navOpen} onClose={() => setNavOpen(false)} />
    </div>
  );
}
